<template>
    <b-modal id="editModal" size="md" body-class="px-0" ref="modal" :title="`Edit ${name}`" v-model="show" @ok="handleSubmit" @hide="close"
             @shown="focusNameInput">

        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary">Account Details</h6>
            </div>
            <div class="card-body">

                <div class="form-group">
                    <label for="nameInput">Name</label>
                    <div class="position-relative">
                        <input type="text" id="nameInput" ref="nameInput" name="name" v-validate="validation.name" v-model="name"
                               placeholder="Your Name" class="form-control" required>
                        <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group">
                    <label for="emailInput">Email Address</label>
                    <div class="position-relative">
                        <input type="email" id="emailInput" name="email" v-validate="validation.email" v-model="email"
                               placeholder="Email Address" class="form-control" autocomplete="false">
                        <div class="invalid-feedback" v-if="errors.has('email')">{{ errors.first('email') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group">
                    <label for="emailInput">Password</label>
                    <div class="position-relative">
                        <input type="password" id="passwordInput" name="password" v-model="password" v-validate="{}"
                               placeholder="Password" class="form-control" autocomplete="false">
                        <div class="invalid-feedback" v-if="errors.has('password')">{{ errors.first('password') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>
            </div>
        </div>


        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary">{{ role.title }} details</h6>
            </div>
            <div class="card-body">
                <div class="form-group" v-if="hasBusinessName">
                    <label for="nameInput">Business Name</label>
                    <div class="position-relative">
                        <input type="text" id="businessNameInput" name="business_name" v-validate="validation.business_name" v-model="business_name"
                               placeholder="Business Name" data-vv-as="business name" class="form-control" required>
                        <div class="invalid-feedback" v-if="errors.has('business_name')">{{ errors.first('business_name') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group" v-if="isA('processor')">
                    <label>Represented Processor</label>
                    <div class="position-relative">
                        <VueSelect
                                v-model="managedProcessor"
                                :options="allProcessors"
                                placeholder="Represented Processor"
                                label="name"
                                track-by="id"
                        >
                        </VueSelect>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group" v-if="isA('association')">
                    <label>Represented Association</label>
                    <div class="position-relative">
                        <VueSelect
                                v-model="managedAssociation"
                                :options="allAssociations"
                                placeholder="Represented Association"
                                label="name"
                                track-by="id"
                        >
                        </VueSelect>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group" v-if="isA('grower', 'contractor')">
                    <label>Region</label>
                    <div class="position-relative">
                        <VueSelect
                                v-validate="validation.region"
                                data-vv-name="region"
                                v-model="region"
                                :options="allCountries"
                                :allow-empty="false"
                                deselect-label=""
                                placeholder="Region"
                                group-values="regions"
                                group-label="name"
                                label="name"
                                track-by="id"
                        >
                        </VueSelect>
                        <div class="invalid-feedback" v-if="errors.has('region')">{{ errors.first('region') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group" v-if="isA('grower')">
                    <label>Processor Managed</label>
                    <div class="custom-control custom-radio mb-1">
                        <input type="radio" id="processor_managed" :value="true" name="is_processor_managed"
                               v-model="is_processor_managed" class="custom-control-input">
                        <label class="custom-control-label" for="processor_managed">Managed</label>
                    </div>
                    <div class="custom-control custom-radio">
                        <input type="radio" id="processor_independent" :value="false" name="is_processor_managed"
                               v-model="is_processor_managed" class="custom-control-input">
                        <label class="custom-control-label" for="processor_independent">Independent</label>
                    </div>
                </div>

                <div class="form-group " v-if="isA('grower')">
                    <label for="physicalAddressInput">Physical Address</label>
                    <div class="position-relative">
                        <textarea id="physicalAddressInput" name="physical_address" v-model="physical_address"
                                  placeholder="Physical Address" class="form-control" v-autosize></textarea>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div v-if="isA('auditor', 'admin', 'developer')" class="text-secondary text-center">
                    No options available for this role
                </div>
            </div>
        </div>

        <div class="card card-flat mb-0" v-if="isA('grower')">
            <div class="bg-light">
                <h6 class="card-header text-secondary">Subscription details</h6>
            </div>
            <div class="card-body py-2">
                <b-table small hover stacked="md" :items="activeSubscriptions" :fields="table.fields" :show-empty="true">
                    <template slot="name" slot-scope="data">
                        {{ data.item.package.name }}
                    </template>
                    <template slot="price" slot-scope="data">
                        <template v-if="data.item.status === 'trial_active'">
                            Free until {{ data.item.trial_ends_on | date_format }}
                        </template>
                        <template v-else>{{ data.item.package.price | money_format }}</template>
                    </template>
                </b-table>

                <p><strong>Total Due:</strong> {{ subscriptionTotals | money_format }}</p>
            </div>
        </div>

        <template slot="modal-footer">
            <button class="btn btn-link text-danger mr-auto" @click="handleDelete" v-if="!user.deleted_at">
                Disable
            </button>
            <button class="btn btn-link text-danger mr-auto" @click="handleRestore" v-if="user.deleted_at">
                Restore
            </button>
            <button class="btn btn-secondary" @click="close">
                Cancel
            </button>
            <button class="btn btn-primary" @click="handleSubmit">
                Save
            </button>
        </template>

    </b-modal>
</template>


<script>
import listCountriesQuery from '@views/Users/CreateModal/queryListCountries.graphql';
import listRolesQuery from '@views/Users/CreateModal/queryListRoles.graphql';

import deleteUserMutation from '@views/Users/EditModal/mutationDeleteUser.graphql';
import restoreUserMutation from '@views/Users/EditModal/mutationRestoreUser.graphql';
import updateUserMutation from '@views/Users/EditModal/mutationUpdateUser.graphql';
import listAssociationsQuery from '@views/Users/EditModal/queryListAssociations.graphql';
import listProcessorsQuery from '@views/Users/EditModal/queryListProcessors.graphql';
import startCase from 'lodash.startcase';


export default {
  name: 'edit-modal',
  apollo: {
    allCountries: {
      query: listCountriesQuery,
      update: data => data.filterCountries.data,
    },
    allProcessors: {
      query: listProcessorsQuery,
      update: data => data.filterProcessors.data,
      skip() {
        return !this.isA('processor');
      },
    },
    allAssociations: {
      query: listAssociationsQuery,
      update: data => data.filterAssociations.data,
      skip() {
        return !this.isA('association');
      },
    },
    allRoles: {
      query: listRolesQuery,
      update: data => data.filterRoles,
    },
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    close: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      loading: 0,
      show: true,
      name: this.user.name,
      email: this.user.email,
      business_name: this.user.business_name,
      physical_address: this.user.physical_address,
      is_processor_managed: this.user.is_processor_managed,
      password: null,
      region: this.$path.get(this, 'user.region'),
      managedProcessor: this.$path.get(this, 'user.managedProcessor'),
      managedAssociation: this.$path.get(this, 'user.managedAssociation'),
      role: this.user.role,
      allCountries: [],
      allRoles: [],
      allProcessors: [],
      allAssociations: [],
      table: {
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'status', label: 'status', formatter: startCase },
          { key: 'price', label: 'Price' },
        ],
      },
      validation: {
        name: { required: true },
        business_name: { required: true },
        role: { required: true },
        region: { required: true },
        is_processor_managed: { required: true },
        email: { required: true, email: true },
      },
    };
  },
  computed: {
    activeSubscriptions() {
      return this.user.subscriptions.filter(subscription => ['trial_active', 'purchased'].includes(subscription.status));
    },
    subscriptionTotals() {
      return this.user.subscriptions
        .filter(subscription => subscription.status === 'purchased')
        .reduce((total, subscription) => total + subscription.package.price, 0);
    },
    hasBusinessName() {
      return !this.isA('processor', 'association');
    },
  },
  methods: {
    isA(...roles) {
      return roles.includes(this.user.role.name);
    },
    handleSubmit() {
      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          return;
        }
        const mutationInput = {
          id: this.user.id,
          name: this.name,
          email: this.email,
        };

        if (this.password !== null && this.password.length > 0) {
          mutationInput.password = this.password;
        }

        if (this.isA('grower', 'contractor')) {
          mutationInput.region_id = this.$path.get(this, 'region.id');
        }

        if (this.isA('grower')) {
          mutationInput.physical_address = this.physical_address;
          mutationInput.is_processor_managed = this.is_processor_managed;
        }

        if (this.isA('processor')) {
          mutationInput.managed_processor_id = this.$path.get(this, 'managedProcessor.id');
        }

        if (this.isA('association')) {
          mutationInput.managed_association_id = this.$path.get(this, 'managedAssociation.id');
        }

        if (this.hasBusinessName) {
          mutationInput.business_name = this.business_name;
        }

        this.$apollo
          .mutate({
            mutation: updateUserMutation,
            variables: {
              input: mutationInput,
            },
          })
          .then(() => {
            this.$notifySuccess('User updated!');
            this.close();
          })
          .catch(err => {
            this.$mapGraphQLError(err);
          })
          .catch(err => {
            console.error(err);
            this.$notifyFailure(
              'Unfortunately, we were unable to do that!',
            );
          });
      });
    },
    handleDelete() {
      this.$apollo
        .mutate({
          mutation: deleteUserMutation,
          variables: {
            input: {
              id: this.user.id,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            deleteUser: {
              __typename: 'User',
              id: this.user.id,
              deleted_at: '1970-01-01 00:00:00',
            },
          },
        })
        .then(() => {
          this.$notifySuccess('User disabled!');
          this.close();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure(
            'Unfortunately, we were unable to do that!',
          );
        });
    },
    handleRestore() {
      this.$apollo
        .mutate({
          mutation: restoreUserMutation,
          variables: {
            input: {
              id: this.user.id,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            restoreUser: {
              __typename: 'User',
              id: this.user.id,
              deleted_at: null,
            },
          },
        })
        .then(() => {
          this.$notifySuccess('User restored!');
          this.close();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure(
            'Unfortunately, we were unable to do that!',
          );
        });
    },

    focusNameInput() {
      this.$refs.nameInput.focus();
    },
  },
};
</script>
